/**
 * storedum.js — the only code that runs on the MERCHANT's page.
 *
 * Hard constraints (enforced by a build assertion, see vite.config.ts):
 *   - no React, no UI framework, no heavy dependency
 *   - small enough that adding it to a Shopify theme is unnoticeable
 *
 * All it does is: mount our hidden bridge iframe, relay commands to it, and
 * re-emit events. Every piece of real logic — tokens, API calls, cart state,
 * UI — lives inside the iframe, on our origin, where a compromised merchant
 * theme cannot reach it.
 */
import {
    POPUP_H,
    PROTOCOL_NAMESPACE,
    PROTOCOL_VERSION,
    isEventMessage,
    isHostCommand,
    isReply,
    isSafeUrl,
    popupWidth,
    type AddToCartInput,
    type AttributionContext,
    type CartSummary,
    type Command,
    type CommandPayloads,
    type CommandType,
    type DeliveryEstimate,
    type EventMessage,
    type EventName,
    type RemoveFromCartInput,
    type ShopifyCart,
    type UpdateQuantityInput,
} from "@storedum/core";
import {
    analyticsAllowed,
    installShopify,
    installSignin,
    serveHostCommand,
    viewedProduct,
    type ShopifyDriver,
    type ShopifyHooks,
    type ShopifyOptions,
} from "./shopify";
import { realVisit, storedSessionId, touchSession, trackSession } from "./session";
import type { BuyDockModule, BuyDockMountOptions, BuyDockPageInput } from "./buydock";
import type { BoxModule, BoxMountOptions } from "./box";

export type { ShopifyOptions, ShopifySigninOptions } from "./shopify";
export type { BuyDockMountOptions, BuyDockPageInput } from "./buydock";
import type { VideosModule, VideosMountOptions } from "./videos";
export type { VideosMountOptions } from "./videos";
export type { BoxMountOptions } from "./box";

/** Replaced at build time; overridable in init() for local development. */
declare const __EMBED_ORIGIN__: string;

const DEFAULT_EMBED_ORIGIN =
    typeof __EMBED_ORIGIN__ === "string" ? __EMBED_ORIGIN__ : "https://embed.storedum.com";

const FRAME_ID = "storedum-bridge";
const COMMAND_TIMEOUT_MS = 15000;

/**
 * The hidden cart attribute that carries the session to the order: Shopify
 * copies cart attributes onto the order (a checkout app's orders too), and the
 * store's order import reads it back into `Orders.session_uuid`. The leading
 * underscore keeps it out of the checkout and order pages.
 */
const SESSION_ATTRIBUTE = "_storedum_session";
/** `<session>@<ms>` of the last initiate-checkout reported. */
const CHECKOUT_MARK = "storedum_checkout_at";
/** The token of the Shopify cart the last checkout app was opened on. */
const CHECKOUT_CART = "storedum_checkout_cart";
/** The theme's own checkout buttons — the ones Shopify's `/cart` form submits through. */
const THEME_CHECKOUT = 'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
/**
 * The mark on the shop URL a checkout app (GoKwik) sends the shopper to after
 * paying (the API's `PLACED_MARK`). Only this page knows the browser's session,
 * which the order carries, so it opens the order's page on the central domain.
 */
const ORDER_PLACED_MARK = "storedum=order-placed";
const DEFAULT_HOSTED_ORIGIN = "https://go.storedum.com";

/**
 * The guest cart token's first-party home (token-cart stores).
 *
 * Cookie + localStorage, under the SAME names the storefront's own guest-cart
 * lib uses (`cart_token`) — on a Storedum storefront both sides then read one
 * copy, and on a third-party site these are simply the SDK's keys. A cookie as
 * well as storage because storefront SSR reads the cookie.
 */
const CART_TOKEN_KEY = "cart_token";

function readPersistedCartToken(): string | null {
    try {
        const fromCookie = document.cookie
            .split("; ")
            .find((r) => r.startsWith(`${CART_TOKEN_KEY}=`))
            ?.split("=")[1];
        if (fromCookie) return fromCookie;
    } catch {
        /* ignore */
    }
    try {
        return window.localStorage.getItem(CART_TOKEN_KEY);
    } catch {
        return null;
    }
}

function persistCartToken(token: string | null): void {
    try {
        document.cookie = token
            ? `${CART_TOKEN_KEY}=${token}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT;`
            : `${CART_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    } catch {
        /* ignore */
    }
    try {
        if (token) window.localStorage.setItem(CART_TOKEN_KEY, token);
        else window.localStorage.removeItem(CART_TOKEN_KEY);
    } catch {
        /* private mode — the cookie alone carries it */
    }
}

export interface StoredumInitOptions {
    /** Tenant id, e.g. "harveni". Selects the store the bridge talks to. */
    store: string;
    /** Override the embed origin (local development only). */
    embedOrigin?: string;
    /** Override the central pages' origin, go.storedum.com (local development only). */
    hostedOrigin?: string;
    debug?: boolean;

    /**
     * The host's guest CART token for this store (token-cart stores). Rides
     * the sign-in handoff as `previous_cart_token` so the guest's cart merges
     * into the account. When omitted, the SDK falls back to the copy it
     * persisted itself from `cart:tokenchanged`.
     */
    previousCartToken?: string;

    /**
     * The host's existing session token for this store, if it has one.
     *
     * A storefront issues guests a token of its own before anyone signs in,
     * and a cart, wishlist and half-filled address hang off it. The handoff
     * passes this along as `previous_auth_token` so the server can merge that
     * work into the account the shopper just proved they own.
     *
     * Omit it and sign-in still succeeds — the shopper simply arrives at an
     * empty cart, having filled one a minute earlier. That is the single most
     * expensive thing this integration can get wrong, which is why it is here
     * and not buried in an options bag somewhere.
     */
    previousAuthToken?: string;

    /**
     * `"shopify"` when this page is a Shopify storefront.
     *
     * The cart of record is then Shopify's own: the drawer reads and edits it
     * over the shop's AJAX Cart API from this page, Checkout goes to Shopify's
     * checkout, and every change is mirrored to the store so its automations
     * see it. Coupons, store credit and gifts — Storedum's engines — stay
     * hidden in the drawer, because Shopify's checkout would not honour them.
     */
    platform?: "shopify";
    /** Shopify page context — routes, customer, theme — from the app embed. */
    shopify?: ShopifyOptions;
}

type Pending = {
    resolve: (value: any) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
};

let options: StoredumInitOptions | null = null;
let embedOrigin = DEFAULT_EMBED_ORIGIN;
let hostedOrigin = DEFAULT_HOSTED_ORIGIN;
let frame: HTMLIFrameElement | null = null;
let frameReady = false;
let readyWaiters: Array<() => void> = [];
const pending = new Map<string, Pending>();
const listeners = new Map<EventName, Set<(payload: any) => void>>();

/** The Shopify cart driver, when `platform: "shopify"`. */
let shopify: ShopifyDriver | null = null;
/** Cart mirror reports in flight, serialised (see `shopifyHooks`). */
let reportChain: Promise<unknown> = Promise.resolve();

/** Signet state as the frames last reported it (`auth:changed`). */
let signedIn = false;
/** A drawer Checkout held while a guest is offered sign-in (Shopify `signin.beforeCheckout`). */
let heldCheckout: { code?: string; provider?: string } | null = null;
/** `<cart token>|<shopper>` last re-sent after a sign-in, so each page load does not repeat it. */
const MIRROR_OWNER_KEY = "storedum_mirror_owner";
/** The latest post-sign-in re-send, queued or not; a held checkout waits on it. */
let following: Promise<void> = Promise.resolve();

/**
 * On Shopify the store's copy of the cart follows the bearer of the report that
 * wrote it, so a cart filled as a guest stays a guest's until the next change.
 * Re-sending it now lands it on the shopper who just signed in.
 */
function followSignIn(user: { user_id?: unknown; global_user_id?: unknown } | null | undefined) {
    const driver = shopify;
    if (!driver) return;
    const who = String(user?.user_id ?? user?.global_user_id ?? "signed-in");
    following = driver
        .get()
        .then((cart: ShopifyCart) => {
            if (!cart?.token || !cart.item_count) return;
            const mark = `${cart.token}|${who}`;
            try {
                if (sessionStorage.getItem(MIRROR_OWNER_KEY) === mark) return;
                sessionStorage.setItem(MIRROR_OWNER_KEY, mark);
            } catch {
                /* private mode — re-sending twice is harmless */
            }
            driver.resend(cart);
        })
        .catch((error) => log("cart not re-sent after sign-in", error?.message || error));
}

function onAuthChanged(payload: { signed_in?: boolean; user?: any } | null | undefined) {
    signedIn = !!payload?.signed_in;
    if (!shopify) return;
    document.documentElement.setAttribute("data-storedum-auth", signedIn ? "signed-in" : "signed-out");
    if (signedIn) followSignIn(payload?.user);
}

let commandSeq = 0;
function nextId(): string {
    commandSeq += 1;
    return `c${commandSeq}_${Math.random().toString(36).slice(2, 8)}`;
}

function log(...args: unknown[]) {
    if (options?.debug) console.log("[storedum]", ...args);
}

function readCookie(name: string): string | undefined {
    const match = document.cookie.split("; ").find((row) => row.startsWith(name + "="));
    return match ? match.split("=").slice(1).join("=") : undefined;
}

/**
 * The shopper's session id lives on the MERCHANT's page, so every event from
 * this visit joins up in the funnel reports — the same role
 * `sessionStorage.site_session_uuid` plays on the storefront today, under the
 * storefront's rule: 30 minutes without activity starts a new one (session.ts).
 */
function sessionUuid(): string {
    return touchSession(Date.now()).id;
}

/** The session counter runs on this page (a real visit the shopper consented to measure). */
let sessionsOn = false;

/** Let a step finish, but never hold the shopper up longer than this. */
function bounded(work: Promise<unknown>, ms = 1500): Promise<unknown> {
    return Promise.race([work, new Promise((resolve) => setTimeout(resolve, ms))]);
}

/**
 * Put the session on Shopify's cart, where the order will find it — once per
 * session: a cart already carrying it is left alone. Done at the first
 * add-to-cart and again before checkout, so the cart a checkout app reads
 * already has it.
 */
function stampCart(cart: ShopifyCart | null | undefined): Promise<unknown> {
    const driver = shopify;
    if (!driver || !sessionsOn || !cart) return Promise.resolve();
    const id = sessionUuid();
    if (cart.attributes?.[SESSION_ATTRIBUTE] === id) return Promise.resolve();
    return driver.attributes({ [SESSION_ATTRIBUTE]: id }).catch((error) => log("session not put on the cart", error?.message || error));
}

/**
 * The funnel's initiate-checkout. One checkout can announce itself more than
 * once (the drawer's button, then the checkout app opening), so a session
 * reports it at most once a minute — the store dedupes as well.
 */
function reportCheckout(): void {
    const driver = shopify;
    if (!driver || !realVisit()) return;
    const id = sessionUuid();
    const now = Date.now();
    try {
        const [lastId, at] = (sessionStorage.getItem(CHECKOUT_MARK) || "").split("@");
        if (lastId === id && now - Number(at) < 60000) return;
        sessionStorage.setItem(CHECKOUT_MARK, `${id}@${now}`);
    } catch {
        /* private mode — the store's dedupe still holds */
    }
    driver.report("checkout").catch((error) => log("checkout not recorded", error?.message || error));
}

/** The Shopify cart a checkout app was opened on: GoKwik keeps its token on the order. */
function rememberCheckoutCart(): void {
    const driver = shopify;
    if (!driver) return;
    (driver.cart ? Promise.resolve(driver.cart) : driver.get())
        .then((cart) => {
            try {
                if (cart?.token) sessionStorage.setItem(CHECKOUT_CART, cart.token);
            } catch {
                /* private mode */
            }
        })
        .catch(() => undefined);
}

/**
 * The order's page on the central domain, carrying what the order holds of
 * this browser: the sessions it can carry (the one checkout was reported in,
 * and the tab's own) and the cart it was placed from — plus, when a checkout
 * app was about to send the shopper somewhere, that destination, which the
 * page falls back to if the order cannot be found. In the fragment, so none of
 * it reaches a server log. The page waits there until the order arrives from Shopify.
 */
function placedOrderUrl(store: string, hostedOrigin: string, back?: string): string {
    const sessions: string[] = [];
    let cart = "";
    try {
        const [checkedOut] = (sessionStorage.getItem(CHECKOUT_MARK) || "").split("@");
        if (checkedOut) sessions.push(checkedOut);
        cart = sessionStorage.getItem(CHECKOUT_CART) || "";
    } catch {
        /* private mode */
    }
    const current = storedSessionId();
    if (current && !sessions.includes(current)) sessions.push(current);
    cart = cart || shopify?.cart?.token || "";
    const parts: string[] = [];
    if (sessions.length) parts.push(`s=${sessions.map(encodeURIComponent).join(",")}`);
    if (cart) parts.push(`c=${encodeURIComponent(cart)}`);
    if (back) parts.push(`r=${encodeURIComponent(back)}`);
    return `${hostedOrigin}/o/${encodeURIComponent(store)}/placed${parts.length ? `#${parts.join("&")}` : ""}`;
}

/** On the after-order page (a shop URL carrying the mark), leave for the order's page. */
function openPlacedOrder(store: string, hostedOrigin: string): boolean {
    if (!window.location.href.includes(ORDER_PLACED_MARK)) return false;
    const cover = document.createElement("div");
    cover.style.cssText = "position:fixed;inset:0;z-index:2147483647;background:#fff;";
    (document.body || document.documentElement).appendChild(cover);
    window.location.replace(placedOrderUrl(store, hostedOrigin));
    return true;
}

function hostOf(origin: string): string {
    try {
        return new URL(origin).hostname;
    } catch {
        return "";
    }
}

/**
 * Read fresh on EVERY command, not once at init: `_fbc` only appears after an
 * ad click, which can land mid-session. Capturing once would lose it.
 */
function attribution(): AttributionContext {
    return {
        session_uuid: sessionUuid(),
        _fbp: readCookie("_fbp"),
        _fbc: readCookie("_fbc"),
    };
}

function onMessage(event: MessageEvent) {
    if (event.origin !== embedOrigin) return;
    const data = event.data;
    // The shoppable videos' frames (videos.ts).
    if (videosModule?.message(event)) return;
    // The buy dock's frames: their actions and footprints (buydock.ts).
    if (buyDockModule?.message(event, isHostCommand)) return;
    // A bundle box's frames (box.ts).
    if (boxModule?.message(event, isHostCommand)) return;

    // The drawer or the chat asking THIS page to touch the Shopify cart. Only
    // frames we mounted may ask: the bridge never does, and a stray frame on our
    // origin must not be able to edit the shopper's cart through us. Leaving the
    // chat out made its Add to cart time out and fall back to asking the
    // assistant, which then wrote the Storedum cart instead of Shopify's.
    if (isHostCommand(data)) {
        const fromOurFrame =
            (panelFrame && event.source === panelFrame.contentWindow) ||
            (chatFrame && event.source === chatFrame.contentWindow);
        if (shopify && fromOurFrame) {
            serveHostCommand(shopify, data, event.source as Window, embedOrigin, shopifyHooks());
        }
        return;
    }

    if (isReply(data)) {
        const waiter = pending.get(data.id);
        if (!waiter) return;
        pending.delete(data.id);
        clearTimeout(waiter.timer);
        if (data.ok) waiter.resolve(data.data);
        else waiter.reject(new Error(data.error));
        return;
    }

    // The account's side panel: its size, and taps relayed to the account frame.
    if (isEventMessage(data) && accountPanel && event.source === accountPanel.contentWindow) {
        const payload = (data.payload || {}) as { height?: unknown };
        if (data.event === "account:frame") {
            if (typeof payload.height === "number" && payload.height > 0) accountPanel.style.height = `${Math.ceil(payload.height)}px`;
        } else if (data.event === "account:ready") {
            if (accountPanelState) postAccount(accountPanel, "account:panel", accountPanelState);
        } else if (data.event === "account:go" || data.event === "account:logout") {
            postAccount(accountFrame, data.event, data.payload ?? {});
        }
        return;
    }

    // The account page's requests, honoured only from the frame we mounted.
    if (isEventMessage(data) && accountFrame && event.source === accountFrame.contentWindow) {
        const payload = (data.payload || {}) as { height?: unknown; url?: unknown };
        if (data.event === "account:frame") {
            if (typeof payload.height === "number" && payload.height > 0) accountFrame.style.height = `${Math.ceil(payload.height)}px`;
            sendAccountViewport();
            return;
        }
        if (data.event === "account:slot") {
            const s = data.payload as { top?: unknown; left?: unknown; width?: unknown; height?: unknown } | null;
            accountSlot = s && Number(s.width) > 0 ? { top: Number(s.top) || 0, left: Number(s.left) || 0, width: Number(s.width), height: Number(s.height) || 0 } : null;
            placeAccountPanel();
            return;
        }
        if (data.event === "account:panel") {
            accountPanelState = data.payload;
            postAccount(accountPanel, "account:panel", accountPanelState);
            return;
        }
        if (data.event === "account:login") {
            account.login();
            return;
        }
        if (data.event === "account:navigate") {
            // Only to the store's orders on the hosted order page: this is the line
            // that turns a frame's message into a navigation of the merchant's tab.
            const url = typeof payload.url === "string" ? payload.url : "";
            if (url.startsWith(`${hostedOrigin}/o/${encodeURIComponent(options?.store || "")}/`)) window.location.href = url;
            else console.error("[storedum] refusing to open", url);
            return;
        }
        if (data.event === "account:route") {
            // A new section starts at its top, wherever the page was scrolled to, just below the store's header.
            const top = accountFrame.getBoundingClientRect().top;
            if (top < accountChrome) window.scrollTo({ top: Math.max(0, window.scrollY + top - accountChrome) });
        }
    }

    if (isEventMessage(data)) {
        if (data.event === "bridge:ready") {
            frameReady = true;
            readyWaiters.forEach((fn) => fn());
            readyWaiters = [];
        }
        // The guest cart's token changed (token-cart stores). Persisted HERE,
        // on the host page, because first-party storage is the only kind that
        // reliably survives inside a third-party embed — and under the same
        // names the storefront's own guest-cart lib uses, so both sides read
        // one copy. Handed back at the next bridge.init.
        if (data.event === "cart:tokenchanged") {
            const token = (data.payload as { cart_token?: string | null } | null)?.cart_token;
            persistCartToken(token ?? null);
        }
        // Signed in: the guest cart merged server-side, so the token is dead —
        // it must not follow the NEXT guest on this device into their sign-in.
        if (data.event === "auth:changed" && (data.payload as any)?.signed_in) {
            persistCartToken(null);
        }
        if (data.event === "auth:changed") onAuthChanged(data.payload as any);
        // Only the host page can dismiss the overlay it created, so the drawer
        // asks rather than closing itself. Returning here on purpose:
        // `closePanel` is what tells the page, so letting this fall through to
        // the generic forward below would deliver it twice.
        //
        // Honored only from the ACTIVE panel. The cart drawer emits its close
        // right after `panel:checkout` — and a guest-gating host answers that
        // checkout by mounting the SIGN-IN panel, which replaces the drawer's
        // iframe. The drawer's trailing close then arrives from a frame that
        // no longer exists, and closing on it killed the sign-in the shopper
        // just asked for ("tapped Checkout, everything closed, nothing
        // happened"). A dying panel must not close its successor.
        if (data.event === "chat:frame" && chatFrame && event.source === chatFrame.contentWindow) {
            const size = (data.payload || {}) as { open?: boolean; width?: number; height?: number };
            chatOpen = !!size.open;
            // 0 is a real answer (nothing to show), not a missing one: `|| 106`
            // turned it into an invisible square that swallowed clicks.
            const px = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 106);
            sizeChatFrame(px(size.width), px(size.height));
            notify(data.event, data.payload);

            return;
        }

        if (data.event === "panel:close") {
            if (!panelFrame || event.source === panelFrame.contentWindow) {
                closePanel();
            }

            return;
        }
        // The panel asked for one of the store's legal pages. Same tab, as a
        // link should behave — the shopper can come back and sign in after
        // reading, and the panel reopens where it left off.
        if (data.event === "panel:navigate") {
            const target = (data.payload as { url?: unknown } | null)?.url;
            // Checked here as well as at the source: this value began life in
            // a merchant's config, and this line is the one that turns it into
            // a navigation. `javascript:` in a terms field would otherwise run
            // on the merchant's own origin.
            if (isSafeUrl(target)) {
                closePanel();
                window.location.href = target;
            } else if (target !== undefined) {
                console.error("[storedum] refusing to open", target);
            }
        }
        // The card measured itself. Only a modal cares: the frame has to BE the
        // card, or the transparent margin around it eats clicks meant for the
        // backdrop.
        if (data.event === "panel:size" && panelFrame) {
            const size = data.payload as
                | { width?: number; height?: number; radius?: string }
                | null;
            // Only a modal is sized by its card, and only in HEIGHT: the
            // width came from the viewport before the card ever rendered, and
            // echoing the card's measurement back would just be the host
            // reading its own instruction and, on the next resize, pinning the
            // frame to a width the viewport no longer has. A drawer owns a
            // fixed edge of the screen and keeps it.
            if (panelHolder && size?.height) {
                panelHolder.style.height = `${size.height}px`;
            }
            // The radius applies to BOTH. The frame is a square box around a
            // rounded card, so every corner cut shows whatever the host paints
            // behind it — a drawer's white surface being the one that bit us:
            // a hard white square poking out from behind each soft corner.
            // Tracing the card's own curve makes the seam disappear whatever
            // the card's design is, and keeps working if a merchant ever
            // changes it.
            if (size?.radius) {
                panelFrame.style.borderRadius = size.radius;
                // The frame paints nothing itself, so the radius has to reach
                // whatever does — otherwise the drawer's white sits square
                // behind a rounded frame and nothing has changed.
                if (panelFrame.parentElement) {
                    panelFrame.parentElement.style.borderRadius = size.radius;
                }
            }
        }
        log("event", data.event, data.payload);
        notify(data.event, data.payload);
        // On Shopify the drawer's Checkout means Shopify's checkout. After the
        // page's listeners, so a host that wants to intercept has already run.
        if (shopify && data.event === "cart:checkout") {
            // The drawer hands over the code the shopper typed; Shopify
            // validates and prices it at checkout. It also says which checkout
            // the store wants (cart designer → Checkout): its checkout app,
            // such as GoKwik, or Shopify's own.
            const payload = data.payload as { coupon_code?: string; checkout_provider?: string } | undefined;
            const code = payload?.coupon_code || undefined;
            const provider = payload?.checkout_provider || undefined;
            // The tap is the funnel's initiate-checkout, whatever follows it.
            reportCheckout();
            // A guest is offered sign-in first; whichever way the panel closes,
            // checkout follows (see closePanel). The drawer's trailing
            // panel:close comes from a frame this replaces, so it is ignored.
            if (shopify.options.signin?.beforeCheckout && !signedIn && !shopify.options.customerId) {
                heldCheckout = { code, provider };
                account.login();
            } else {
                // The session on the cart first, so the order the checkout
                // creates carries it. Normally already there since the first add.
                const driver = shopify;
                bounded(stampCart(driver.cart)).then(() => driver.checkout(code, provider));
            }
        }
    }
}

/** What the Shopify driver may do to this page; built lazily so it can reach
 *  functions declared below. */
function shopifyHooks(): ShopifyHooks {
    return {
        openCart: () => cart.open(),
        closePanel,
        notify,
        // Only a cart drawer on screen is pushed at. A closed one re-reads when
        // it is next opened (cart.open asks), so a change made meanwhile costs
        // nothing.
        refreshPanel: () => {
            if (panelShown && panelKey.startsWith("/cart.html") && panelFrame?.contentWindow) {
                panelFrame.contentWindow.postMessage(
                    { ns: PROTOCOL_NAMESPACE, v: PROTOCOL_VERSION, type: "panel.refresh" },
                    embedOrigin,
                );
            }
        },
        // One at a time, in order: every report is a full snapshot and the
        // store keeps the last one it receives, so two fired back to back (a
        // variant switch is an add and a remove) must not land out of order.
        report: (input) => {
            reportChain = reportChain
                .then(() => send("shopify.cartEvent", { ...input, attribution: attribution() }))
                .catch((error) => log("cart event not mirrored", error?.message || error));
            // A cart with something in it is a cart that may become an order.
            if (input.event === "add") void stampCart(input.cart);
        },
        // The chain already swallows its own failures, so awaiting it can only
        // wait — never reject — and a drawer read is never blocked by a mirror
        // that could not be delivered.
        flush: () => reportChain.then(() => undefined),
        log,
    };
}

/**
 * Hand an event to the page's listeners.
 *
 * Separate from `onMessage` because not every event this SDK reports comes
 * from a frame — see `closePanel`.
 */
function notify(event: EventName, payload: unknown) {
    if (event === "cart:updated") buyDockModule?.cart(payload as { total_items?: number });
    listeners.get(event)?.forEach((fn) => {
        try {
            fn(payload);
        } catch (e) {
            console.error("[storedum] listener failed", e);
        }
    });
}

function whenReady(): Promise<void> {
    if (frameReady) return Promise.resolve();
    return new Promise((resolve) => readyWaiters.push(resolve));
}

function send<T extends CommandType>(type: T, payload: CommandPayloads[T]): Promise<any> {
    if (!options) {
        return Promise.reject(new Error("storedum.init({ store }) must be called first"));
    }
    return whenReady().then(
        () =>
            new Promise((resolve, reject) => {
                const id = nextId();
                const message: Command<T> = {
                    ns: PROTOCOL_NAMESPACE,
                    v: PROTOCOL_VERSION,
                    id,
                    type,
                    payload,
                };
                const timer = setTimeout(() => {
                    pending.delete(id);
                    reject(new Error(`storedum: "${type}" timed out`));
                }, COMMAND_TIMEOUT_MS);

                pending.set(id, { resolve, reject, timer });
                frame?.contentWindow?.postMessage(message, embedOrigin);
                log("->", type, payload);
            }),
    );
}

function mountFrame() {
    const existing = document.getElementById(FRAME_ID) as HTMLIFrameElement | null;
    if (existing) {
        frame = existing;
        return;
    }

    const el = document.createElement("iframe");
    el.id = FRAME_ID;
    el.src = `${embedOrigin}/bridge.html?store=${encodeURIComponent(options!.store)}${freshParam()}`;
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("tabindex", "-1");
    el.setAttribute("title", "Storedum bridge");
    el.style.cssText =
        "position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;";

    el.addEventListener("load", () => {
        // Re-assert our origin explicitly: a strict Referrer-Policy on the
        // merchant's site can strip the referrer the bridge would otherwise
        // use to identify us.
        const message: Command<"bridge.init"> = {
            ns: PROTOCOL_NAMESPACE,
            v: PROTOCOL_VERSION,
            id: nextId(),
            type: "bridge.init",
            payload: {
                origin: window.location.origin,
                attribution: attribution(),
                previous_auth_token: options?.previousAuthToken,
                previous_cart_token: options?.previousCartToken ?? readPersistedCartToken() ?? undefined,
            },
        };
        el.contentWindow?.postMessage(message, embedOrigin);
    });

    document.body.appendChild(el);
    frame = el;
}

export function init(opts: StoredumInitOptions): void {
    if (options) {
        log("already initialised — ignoring");
        return;
    }
    if (!opts || !opts.store) throw new Error("storedum.init requires { store }");

    options = opts;
    embedOrigin = (opts.embedOrigin || DEFAULT_EMBED_ORIGIN).replace(/\/$/, "");
    hostedOrigin = (opts.hostedOrigin || DEFAULT_HOSTED_ORIGIN).replace(/\/$/, "");
    if (opts.platform === "shopify" && openPlacedOrder(opts.store, hostedOrigin)) return;
    window.addEventListener("message", onMessage);

    // An OPEN modal follows the window. Without this the frame kept whatever
    // width it was opened at, so dragging Chrome's device toolbar left a phone
    // sheet on a desktop window and a clipped desktop card on a phone one.
    // Cheap enough to leave attached: it does nothing unless a modal is up.
    window.addEventListener("resize", () => {
        if (panelHolder) sizeModal(panelHolder, false);
    });

    if (document.body) mountFrame();
    else document.addEventListener("DOMContentLoaded", mountFrame, { once: true });

    const views = queuedViews;
    queuedViews = [];
    views.forEach((view) => send("track.productView", view).catch((error) => log("product view not kept", error?.message || error)));

    const dock = queuedDock;
    queuedDock = null;
    if (dock) buydock.mount(dock);

    const queued = queuedBox;
    queuedBox = null;
    if (queued) box.mount(queued);

    const vids = queuedVideos;
    queuedVideos = [];
    vids.forEach((v) => videos.mount(v));

    const accountMount = queuedAccount;
    queuedAccount = null;
    if (accountMount) accountPage.mount(accountMount);

    if (opts.platform === "shopify") {
        const install = () => {
            shopify = installShopify(opts.shopify || {}, shopifyHooks());
            log("shopify driver installed", opts.shopify);
            const viewed = viewedProduct();
            if (viewed && analyticsAllowed()) {
                send("shopify.productView", {
                    shopify_product_id: viewed.product,
                    shopify_variant_id: viewed.variant ?? null,
                    attribution: attribution(),
                }).catch((error) => log("product view not recorded", error?.message || error));
            }
            if (opts.shopify?.signin) {
                installSignin(opts.shopify, {
                    openLogin: () => account.login(),
                    logout: () => {
                        account.logout().catch((error) => log("sign out failed", error?.message || error));
                    },
                });
            }
            if (realVisit()) {
                // A checkout app announces itself however it was opened — the
                // drawer, a theme button, Buy it now: GoKwik posts
                // `checkout_initiated` from its frame. The cart it reads gets
                // the session too, for any add made outside the drawer.
                //
                // Once the order is placed GoKwik keeps its success screen up
                // about 3 s, so the store's pixels fire, then asks this page to
                // open Shopify's order page (`redirect`). That redirect goes to
                // the shopper's order page on the central domain instead.
                let gokwikOrdered = false;
                window.addEventListener("message", (event) => {
                    if (!/(^|\.)gokwik\.co$/.test(hostOf(event.origin))) return;
                    const type = event.data?.type;
                    if (type === "checkout_initiated") {
                        reportCheckout();
                        rememberCheckoutCart();
                        void stampCart(shopify?.cart);
                    } else if (type === "orderSuccess") {
                        gokwikOrdered = true;
                    } else if (type === "redirect" && gokwikOrdered) {
                        event.stopImmediatePropagation();
                        let back = "";
                        try {
                            const url = event.data?.url;
                            if (typeof url === "string" && url) back = new URL(url, window.location.href).href;
                        } catch {
                            /* no destination to fall back to */
                        }
                        window.location.replace(placedOrderUrl(opts.store, hostedOrigin, back));
                    }
                });
                // The theme's own checkout buttons, for Shopify's checkout.
                // Best effort: the page is already navigating away.
                document.addEventListener(
                    "click",
                    (event) => {
                        if ((event.target as Element | null)?.closest?.(THEME_CHECKOUT)) reportCheckout();
                    },
                    true,
                );
                // The session counter, where the shopper has not declined
                // analytics on the store's consent banner.
                if (analyticsAllowed()) {
                    sessionsOn = true;
                    trackSession({
                        start: (input) => send("session.start", input),
                        update: (input) => send("session.update", input),
                    });
                }
            }
        };
        if (document.body) install();
        else document.addEventListener("DOMContentLoaded", install, { once: true });
    }
}

/**
 * `&fresh=1`, when the merchant's own page carries it.
 *
 * Our frames cache the store config in their own `sessionStorage`, which a
 * reload does not clear — so a merchant who publishes and refreshes to check
 * is served their old config by their own browser. Forwarding the flag means
 * `?fresh=1` on the storefront reaches the frames that actually hold the
 * cache, which is where anyone would expect to put it.
 *
 * Shopper-invisible: nothing links to it, and all it costs is skipping a
 * cache.
 */
function freshParam(): string {
    try {
        return new URLSearchParams(window.location.search).has("fresh") ? "&fresh=1" : "";
    } catch {
        return "";
    }
}

const PANEL_ID = "storedum-panel";
let panel: HTMLDivElement | null = null;

/** Which panel is mounted, as `path|mode`, so a request for a different one
 *  rebuilds instead of re-showing whatever happened to be there. */
let panelKey = "";

/**
 * The pending `display:none` that finishes a close.
 *
 * Held so reopening can CANCEL it. Without that, a shopper who dismissed the
 * panel and changed their mind inside the fade got it hidden out from under
 * them by the old timer, and every later open found an overlay it thought was
 * already showing.
 */
let closeTimer: ReturnType<typeof setTimeout> | null = null;

/** A panel is on screen — between `openPanel` and `closePanel`. */
let panelShown = false;

/**
 * Put a mounted panel into its open state.
 *
 * Shared by the first mount and every reopen, because they were not shared
 * before and drifted apart badly. Reopening only set `display`, so the panel
 * came back carrying everything the close had left on it: `opacity: 0` on the
 * overlay and the exit transform on the holder. The result was a
 * full-viewport, fixed, invisible layer at z-index 2147483000 — the sign-in
 * appeared not to open, and every click on the merchant's page underneath hit
 * it instead. `display` was wrong too: a modal centres with flex, and "block"
 * would have dropped the card in the corner.
 *
 * So this resets to the closed state explicitly and animates in from there,
 * rather than assuming what a previous close happened to leave behind.
 */
function openPanel(overlay: HTMLElement, holder: HTMLElement | null, modal: boolean) {
    if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
    }
    panelShown = true;

    overlay.style.display = modal ? "flex" : "block";
    overlay.style.pointerEvents = "";
    overlay.style.opacity = "0";
    if (holder) {
        if (modal) {
            holder.style.transform = "scale(.98)";
            holder.style.opacity = "0";
        } else {
            holder.style.transform = "translateX(100%)";
        }
    }

    // Force a reflow so the browser records the closed state, then apply the
    // open state synchronously. requestAnimationFrame would be the prettier
    // way, but it does not fire in a background or non-compositing tab — the
    // drawer would sit there at opacity 0, open but invisible.
    void overlay.offsetHeight;
    overlay.style.opacity = "1";
    if (holder) {
        if (modal) {
            holder.style.transform = "scale(1)";
            holder.style.opacity = "1";
        } else {
            holder.style.transform = "translateX(0)";
        }
    }
}

/** The centred card, while a modal is open. Null for a drawer. */
let panelHolder: HTMLDivElement | null = null;

/**
 * The iframe itself, for as long as a panel is open — drawer or modal.
 *
 * Separate from `panelHolder` because the two answer different questions.
 * `panelHolder` means "this panel is a modal, and its box is the card's": only
 * a modal resizes to its content. This one means "a panel is open at all", and
 * corners belong to every panel, not just centred ones.
 */
let panelFrame: HTMLIFrameElement | null = null;

/**
 * How a panel sits on the merchant's page.
 *
 * `drawer` is the cart: a full-height sheet against the right edge, which is
 * what shoppers expect a cart to be. `modal` is sign-in: a card centred over
 * the dimmed page, so the shop stays visible behind it and the shopper can see
 * they never left. They are different shapes because they answer different
 * questions — "what is in my basket" is a place you visit, "who are you" is an
 * interruption you finish.
 */
type PanelMode = "drawer" | "modal";

/**
 * Size a modal's frame to the viewport.
 *
 * WIDTH is the host's to decide and the host's alone: it is the only side that
 * can see the browser window, since everything the card can measure is the
 * frame we already gave it. Asking the card meant a fixed 342px sheet sitting
 * in the middle of a 555px screen, because from inside 342px is all there was
 * to see.
 *
 * Re-applied on resize, so dragging Chrome's device toolbar across the
 * breakpoint reshapes an OPEN popup. The card watches its own width and
 * redraws itself to match, so the two stay in step without either polling the
 * other.
 *
 * HEIGHT is not set here — only the card knows how tall its current step is,
 * and it reports that. This seeds a plausible one for the seconds before the
 * first report: a cross-origin document has to load, fetch its store config
 * and render first, which on mobile data is not a frame or two.
 */
function sizeModal(holder: HTMLElement, seedHeight: boolean) {
    const width = popupWidth(window.innerWidth);
    holder.style.width = `${width}px`;
    if (seedHeight) {
        // Roughly the phone sheet, roughly the desktop card. Wrong either way,
        // but close enough that the correction reads as a settle rather than a
        // jump.
        holder.style.height = `${width >= POPUP_H ? POPUP_H : 520}px`;
    }
}

/**
 * The visible panel. The overlay chrome lives on the merchant's page (only it
 * can cover the viewport), but every pixel of content is inside our iframe.
 *
 * Styles are set inline on our own elements and namespaced by id — a merchant
 * theme's CSS can be anything, so we neither inherit from it nor leak into it.
 */
function mountPanel(path: string, mode: PanelMode = "drawer") {
    if (!options) throw new Error("storedum.init({ store }) must be called first");

    // Reuse the mounted panel only when it is the SAME panel. It used to be
    // reused unconditionally, so `cart.open()` followed by `account.login()`
    // re-showed the CART: same overlay, same iframe, same drawer chrome, only
    // now answering the wrong question. It also stranded the shopper in drawer
    // mode — a rounded sign-in card inside the drawer's opaque white box,
    // which is where the hard white corners came from.
    const want = `${path}|${mode}`;
    if (panel && panelKey === want) {
        openPanel(panel, panel.firstElementChild as HTMLElement | null, mode === "modal");
        return;
    }
    if (panel) {
        // Different panel: tear the old one down rather than restyle it. The
        // chrome differs in background, position, transform and exit
        // animation, and mutating all of it in place is a longer list to keep
        // in step than simply building the right one.
        panel.remove();
        panel = null;
        panelHolder = null;
        panelFrame = null;
        // Its hide would otherwise fire against a detached node, and — worse —
        // still be pending when the NEW panel wants to cancel one.
        if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
        }
    }

    const modal = mode === "modal";

    const overlay = document.createElement("div");
    overlay.id = PANEL_ID;
    overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,.45);" +
        "opacity:0;transition:opacity .18s ease;" +
        // A modal centres its card and lets the page show through around it.
        // `overflow:auto` matters on a short screen: the card is as tall as its
        // step, and a shopper must never be unable to reach the button.
        (modal
            ? "display:flex;align-items:center;justify-content:center;padding:20px;" +
              "box-sizing:border-box;overflow:auto;"
            : "display:block;");

    const holder = document.createElement("div");
    holder.style.cssText = modal
        ? // No background and no shadow of our own: the card inside the frame
          // draws its own corners and shadow, and a white box behind it would
          // show as a square halo at every rounded corner.
          "max-width:100%;max-height:100%;" +
          "flex:none;transform:scale(.98);opacity:0;" +
          "transition:transform .18s ease,opacity .18s ease;"
        : "position:absolute;top:0;right:0;height:100%;width:100%;max-width:420px;" +
          "background:#fff;box-shadow:-2px 0 24px rgba(0,0,0,.18);" +
          "transform:translateX(100%);transition:transform .22s ease;";

    const iframe = document.createElement("iframe");
    // `path` may already carry a query (the cart demo), so pick the separator.
    const sep = path.includes("?") ? "&" : "?";
    iframe.src = `${embedOrigin}${path}${sep}store=${encodeURIComponent(options.store)}${freshParam()}`;
    // One panel serves both surfaces, so the title has to follow the path — a
    // screen reader announcing "Cart" over the sign-in form is simply wrong.
    iframe.title = path.includes("login") ? "Sign in" : path.includes("chat") ? "Chat" : "Cart";
    // Permissions Policy does not cross an origin boundary on its own: without
    // this the panel's Clipboard API calls reject, and "Copy code" falls back
    // to a deprecated path that some browsers have already dropped. Granted
    // narrowly — write only, and only to our own origin.
    iframe.allow = `clipboard-write ${embedOrigin}`;
    // `allow-transparency` is not a thing any more; a frame is transparent when
    // the document inside paints no background, which popup.css arranges.
    iframe.style.cssText = "width:100%;height:100%;border:0;display:block;background:transparent;";

    holder.appendChild(iframe);
    overlay.appendChild(holder);
    document.body.appendChild(overlay);

    // Clicking the dimmed area closes, matching every drawer shoppers know.
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) closePanel();
    });

    // Escape too. The listener is on the merchant's document because that is
    // where focus lives until the shopper clicks into our frame; the frame
    // handles its own Escape, and asks us to close via `panel:close`.
    if (modal) {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || !panel || panel.style.display === "none") return;
            closePanel();
            document.removeEventListener("keydown", onKey);
        };
        document.addEventListener("keydown", onKey);
    }

    if (modal) sizeModal(holder, true);
    openPanel(overlay, holder, modal);

    panel = overlay;
    panelHolder = modal ? holder : null;
    panelFrame = iframe;
    panelKey = want;
}

function closePanel() {
    if (!panel) return;
    panelShown = false;
    const holder = panel.firstElementChild as HTMLElement | null;
    panel.style.opacity = "0";
    // Stop taking clicks the moment it starts leaving, not when it finishes.
    // It stays a full-viewport fixed layer for the whole fade, and a shopper
    // who has just dismissed a panel expects the page under it to work — not
    // to swallow the next fifth of a second of their clicks.
    panel.style.pointerEvents = "none";
    if (holder) {
        // A modal shrinks back where it grew from; a drawer slides out the
        // edge it came in on. Using the drawer's exit on a centred card threw
        // it sideways off the screen.
        if (panelHolder) {
            holder.style.transform = "scale(.98)";
            holder.style.opacity = "0";
        } else {
            holder.style.transform = "translateX(100%)";
        }
    }
    const el = panel;
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
        el.style.display = "none";
        closeTimer = null;
    }, 200);

    /**
     * Tell the page, whatever closed it.
     *
     * `panel:close` used to reach listeners only when it arrived as a MESSAGE
     * from the frame — so a shopper who dismissed the panel by clicking the
     * backdrop or pressing Escape closed it without the host ever hearing.
     * A host that resumes an interrupted checkout when the panel goes away
     * therefore waited forever on the two most natural ways to dismiss it.
     *
     * Here instead, because this function is the one thing every close path
     * goes through, and it has already returned early if there was no panel
     * open — so this cannot fire spuriously.
     */
    notify("panel:close", {});

    if (heldCheckout && shopify) {
        const driver = shopify;
        const { code, provider } = heldCheckout;
        heldCheckout = null;
        const go = () => {
            bounded(stampCart(driver.cart)).then(() => driver.checkout(code, provider));
        };
        // Signed in: give the re-sent cart a moment to reach the store, so
        // what they order is already theirs. Bounded — checkout never waits on us.
        if (signedIn) {
            const settled = following.then(() => reportChain);
            Promise.race([settled, new Promise((resolve) => setTimeout(resolve, 2500))]).then(go, go);
        } else {
            go();
        }
    }
}

export const account = {
    /**
     * Tell the bridge the host's CURRENT session token.
     *
     * `init` takes one, and for a page that loads and then signs somebody in
     * that is enough. It stops being enough the moment the host's own session
     * changes without a page load: a shopper signs out, another signs in, and
     * the handoff still carries the first one's token — which the server
     * resolves to a user and whose cart it then moves into the second account.
     *
     * Call it whenever the host's token changes, and with nothing to clear it.
     */
    setPreviousAuthToken(token?: string) {
        return send("bridge.setHostToken", { previous_auth_token: token });
    },

    /**
     * The host's guest CART token, kept current for the same reason as the
     * bearer above: it is what the sign-in handoff merges the guest's cart
     * from, and a stale one is a different shopper's cart.
     */
    setPreviousCartToken(token?: string) {
        return send("bridge.setHostCartToken", { previous_cart_token: token });
    },

    /** Opens the sign-in panel. Cross-store identity lives behind this. */
    login() {
        mountPanel("/login.html", "modal");
    },

    /**
     * You signed someone in yourself. Make it count everywhere.
     *
     * For the flows that already know who the shopper is and so never open the
     * panel: an abandoned-cart link, an order-tracking link opened on a phone
     * that has never seen this store, an OTP answered inside a chat widget.
     * They verify against your own API and leave you holding a session for
     * ONE store — the shopper is a stranger again on the next one.
     *
     * Mint a reverse handoff for that session (`POST
     * /Users/SsoMintReverseHandoff`, with the token you just received) and
     * pass it here. Redeeming it inside the frame is what makes the identity
     * cookie first-party, which is the only reason this works in Safari.
     *
     * Send `authToken` too: it is the session the bridge itself will use, and
     * without it the cart it reports is still the guest's.
     */
    adoptSession(input: { handoff: string; authToken?: string }) {
        return send("account.adoptSession", {
            handoff: input.handoff,
            auth_token: input.authToken,
        });
    },

    /**
     * Sign the shopper out.
     *
     * Default is this store only — they stay recognised on other Storedum
     * stores. Pass `{ everywhere: true }` to revoke the shared identity too.
     *
     * Either way auto sign-in is suppressed here afterwards, so a reload does
     * not quietly sign them back in.
     */
    logout(options: { everywhere?: boolean } = {}) {
        return send("account.logout", {
            everywhere: options.everywhere === true,
            attribution: attribution(),
        });
    },
};

/**
 * Where the shopper is, and when their order would arrive.
 *
 * The pincode is kept once for every Storedum store, so a shopper who told one
 * of them has told all of them. A storefront can read it on load and put a real
 * date on a product page instead of an empty "check your pincode" box.
 */
export const delivery = {
    /**
     * The saved pincode, or null. Pass `set` to store one, or `null` to forget.
     *
     * Only ever store something the SHOPPER gave you. A guess written here
     * becomes indistinguishable from a fact on every other store they visit.
     */
    pincode(set?: string | null): Promise<{ pincode: string | null }> {
        return send("delivery.pincode", set === undefined ? {} : { set });
    },

    /**
     * When this store would deliver — to `pincode`, or to the saved one.
     *
     * Resolves to null when there is nothing to go on or the store could not
     * answer. "We do not deliver there" is NOT null: it comes back as an
     * estimate with `serviceable: false`, because that is something a shopper
     * needs to be told rather than an error to swallow.
     */
    estimate(pincode?: string): Promise<DeliveryEstimate | null> {
        return send("delivery.estimate", pincode ? { pincode } : {});
    },
};

/** Product views reported before init, sent once there is a bridge to take them. */
let queuedViews: Array<CommandPayloads["track.productView"]> = [];

export const track = {
    /**
     * The shopper opened a product page on a host that logs its own views (our
     * storefront records one when it fetches the product). Keeps their recently
     * viewed trail current for the sign-in panel. Safe to call before init.
     */
    productView(input: { product_id: number; name?: string; image?: string; price?: number | string }) {
        const view = { product_id: Number(input?.product_id), name: input?.name, image: input?.image, price: input?.price };
        if (!view.product_id) return Promise.resolve({ remembered: false });
        if (!options) {
            queuedViews.push(view);
            return Promise.resolve({ queued: true });
        }
        return send("track.productView", view);
    },
};

export const cart = {
    /**
     * Opens the cart drawer. Safe to call from a click handler on any theme.
     *
     * `demo` renders the new cart UI against fixtures instead of this store's
     * real cart — the test page uses it; no storefront should.
     */
    open(opts?: { demo?: boolean }) {
        // The guest cart token can be born AFTER init — a storefront-side add
        // mints it into the host cookie, and a drawer opened a beat later
        // would still be reading init's snapshot and show an empty cart. Sync
        // the current one down first; the panel mounts when the bridge has
        // stored it (falls through on timeout — an empty drawer beats none).
        // On Shopify the drawer reads the cart through THIS page (see
        // shopify.ts) — the flag tells the panel which cart to talk to.
        const path = opts?.demo ? "/cart.html?demo=1" : shopify ? "/cart.html?driver=shopify" : "/cart.html";
        // The iframe outlives a close, so a RE-shown panel is a snapshot of
        // its last open — first opened empty, it stayed empty however much
        // the page added since. Ask it to refresh: it remounts its cart view,
        // re-reads the just-synced token and refetches.
        const wasMounted = !!panel && panelKey === `${path}|drawer`;
        const mount = () => {
            mountPanel(path);
            if (wasMounted && panelFrame?.contentWindow) {
                panelFrame.contentWindow.postMessage(
                    { ns: PROTOCOL_NAMESPACE, v: PROTOCOL_VERSION, type: "panel.refresh" },
                    embedOrigin,
                );
            }
        };
        const cartToken = readPersistedCartToken();
        if (opts?.demo || !cartToken || shopify) {
            mount();
            return;
        }
        send("bridge.setHostCartToken", { previous_cart_token: cartToken }).then(mount, mount);
    },
    close() {
        closePanel();
    },
    add(input: AddToCartInput) {
        if (shopify) {
            const id = Number(input.shopify_variant_id);
            if (!id) return Promise.reject(new Error("storedum: cart.add needs shopify_variant_id on a Shopify storefront"));
            return shopify.add(id, input.quantity ?? 1, undefined, "sdk").then(shopifySummary);
        }
        return send("cart.add", { ...input, attribution: attribution() });
    },
    updateQuantity(input: UpdateQuantityInput) {
        if (shopify) {
            const id = Number(input.shopify_variant_id);
            if (!id) return Promise.reject(new Error("storedum: cart.updateQuantity needs shopify_variant_id on a Shopify storefront"));
            return shopify.change(id, input.quantity, "sdk").then(shopifySummary);
        }
        return send("cart.updateQuantity", { ...input, attribution: attribution() });
    },
    remove(input: RemoveFromCartInput) {
        if (shopify) {
            const id = Number(input.shopify_variant_id);
            if (!id) return Promise.reject(new Error("storedum: cart.remove needs shopify_variant_id on a Shopify storefront"));
            return shopify.change(id, 0, "sdk").then(shopifySummary);
        }
        return send("cart.remove", { ...input, attribution: attribution() });
    },
    summary(): Promise<CartSummary> {
        if (shopify) return shopify.get().then(shopifySummary);
        return send("cart.summary", { attribution: attribution() });
    },
};

// ---------------------------------------------------------------------------
// Chat
//
// Unlike the cart, the chat is always ON the page: a launcher that grows into a
// docked popup. So it gets its own frame rather than the panel overlay — no
// backdrop, transparent, and only ever as big as the launcher or the popup,
// because a transparent frame still swallows every click inside it.
// ---------------------------------------------------------------------------

const CHAT_FRAME_ID = "storedum-chat";
/** Below this the popup becomes a full-screen sheet, matching the widget. */
const CHAT_SHEET_MAX = 480;
let chatFrame: HTMLIFrameElement | null = null;
let chatOptions: ChatMountOptions = {};
let chatOpen = false;

export interface ChatMountOptions {
    /** Launcher colour. Anything CSS accepts; the widget derives the rest. */
    brand?: string;
    /** Lift the launcher above a sticky bottom bar, in pixels. */
    bottomOffset?: number;
    /** Open the panel as soon as it loads. */
    open?: boolean;
    /** Show the widget's own launcher. Off when the shop supplies its button. */
    launcher?: boolean;
    /** Pin the conversation to one order, as the tracking page does. */
    order_id?: string;
    order_token?: string;
}

/**
 * The page's width, or 0 when it has none yet.
 *
 * A script can run before layout — in a background tab, or from <head> — and
 * `innerWidth` is 0 there. Treating that as "a very narrow phone" would pick
 * the full-screen sheet, and clamping the frame to it would collapse the chat
 * to nothing, so every caller has to handle 0 as "not known yet".
 */
function viewportWidth(): number {
    return window.innerWidth || document.documentElement?.clientWidth || 0;
}

function viewportHeight(): number {
    return window.innerHeight || document.documentElement?.clientHeight || 0;
}

/**
 * The docked popup's size at each page width, widest first — the same steps
 * the widget uses (chatPopupSize in @storedum/ui). Repeated rather than
 * imported: the SDK may never pull in the UI package, which is React (bundle
 * guard). Every step, or the chat on a shop is smaller than on our storefront
 * from the first width the list leaves out.
 */
const CHAT_POPUP_STEPS: Array<[min: number, width: number, height: number]> = [
    [3200, 640, 968],
    [2560, 580, 892],
    [1920, 520, 812],
    [1536, 470, 744],
    [1280, 430, 700],
    [0, 392, 640],
];

function chatPopupSize(): { width: number; height: number } {
    const width = viewportWidth();
    const step = CHAT_POPUP_STEPS.find(([min]) => width >= min) || CHAT_POPUP_STEPS[CHAT_POPUP_STEPS.length - 1];

    return { width: step[1], height: step[2] };
}

function chatIsSheet(): boolean {
    const width = viewportWidth();

    return width > 0 && width <= CHAT_SHEET_MAX;
}

/** Dock the frame bottom-right, or fill the screen once it is a sheet. */
function sizeChatFrame(width: number, height: number) {
    if (!chatFrame) return;
    const sheet = chatIsSheet() && chatOpen;
    const style = chatFrame.style;
    // The frame has reported, so it may take clicks from here on.
    style.pointerEvents = "";
    if (sheet) {
        style.top = "0";
        style.left = "0";
        style.width = "100%";
        style.height = "100%";
        return;
    }
    // Nothing showing: no footprint at all, rather than a transparent box.
    if (width <= 0 || height <= 0) {
        style.width = "0px";
        style.height = "0px";
        style.pointerEvents = "none";
        return;
    }
    // Never clamp against an unknown viewport: 0 would hide the chat entirely.
    const maxW = viewportWidth() || width;
    const maxH = viewportHeight() || height;
    style.top = "auto";
    style.left = "auto";
    style.width = `${Math.min(width, maxW)}px`;
    style.height = `${Math.min(height, maxH)}px`;
}

export const chat = {
    /**
     * Puts the chat on the page: the launcher shows, and opening it grows the
     * frame into the docked popup. Idempotent.
     */
    mount(opts?: ChatMountOptions) {
        // Nothing to talk to before init(): the store id is what addresses the
        // frame at all.
        if (chatFrame || !options) return;
        chatOptions = opts || {};
        const query: string[] = [`store=${encodeURIComponent(options.store)}`];
        // The frame is sized to the widget, so it cannot read the device width
        // itself — the host picks the form.
        query.push(`view=${chatIsSheet() ? "sheet" : "popup"}`);
        const popup = chatPopupSize();
        query.push(`pw=${popup.width}`, `ph=${popup.height}`);
        if (shopify) query.push("driver=shopify");
        if (chatOptions.brand) query.push(`brand=${encodeURIComponent(chatOptions.brand)}`);
        // A WhatsApp follow-up link lands the shopper here with `sdchat`: the frame
        // redeems it (once) and the chat opens on their conversation.
        const resume = takeChatResumeParam();
        if (resume) query.push(`sdchat=${encodeURIComponent(resume)}`);
        if (chatOptions.open || resume) query.push("open=1");
        if (chatOptions.launcher === false) query.push("launcher=0");
        if (chatOptions.bottomOffset) query.push(`offset=${Math.max(0, Math.round(chatOptions.bottomOffset))}`);
        if (chatOptions.order_id) query.push(`order_id=${encodeURIComponent(chatOptions.order_id)}`);
        if (chatOptions.order_token) query.push(`order_token=${encodeURIComponent(chatOptions.order_token)}`);
        // The page the shopper starts on (origin + path); later moves are posted.
        query.push(`page_url=${encodeURIComponent(location.origin + location.pathname)}`, `page_title=${encodeURIComponent(document.title.slice(0, 160))}`);

        const frame = document.createElement("iframe");
        frame.id = CHAT_FRAME_ID;
        frame.title = "Chat";
        frame.src = `${embedOrigin}/chat.html?${query.join("&")}${freshParam()}`;
        frame.allow = `clipboard-write ${embedOrigin}; geolocation ${embedOrigin}; autoplay ${embedOrigin}`;
        frame.style.cssText = [
            "position:fixed",
            "right:0",
            "bottom:0",
            // Wide enough for the greeting bubble at its natural width. The
            // frame constrains its own contents, so starting launcher-sized
            // would wrap the teaser into a column and then measure THAT.
            "width:280px",
            "height:300px",
            // Inert until the widget reports its real size: the shop stays
            // clickable under this box while chat.html loads.
            "pointer-events:none",
            "border:0",
            "z-index:2147483000",
            "background:transparent",
            "color-scheme:normal",
        ].join(";");
        document.body.appendChild(frame);
        chatFrame = frame;
        window.addEventListener("resize", onChatResize);
        // The frame can't see this page's address: tell it, for the store's team.
        frame.addEventListener("load", postChatPage);
        window.addEventListener("popstate", postChatPage);
    },
    /** Opens the chat, mounting it first if the page has not already. */
    open(opts?: ChatMountOptions) {
        if (!chatFrame) return chat.mount({ ...(opts || {}), open: true });
        post(chatFrame, "chat.open");
    },
    close() {
        if (chatFrame) post(chatFrame, "chat.close");
    },
    /** Takes the chat off the page entirely. */
    unmount() {
        window.removeEventListener("resize", onChatResize);
        window.removeEventListener("popstate", postChatPage);
        chatFrame?.remove();
        chatFrame = null;
        chatOpen = false;
    },
};

/** The page the shopper is on — origin and path only, never the query string. */
function postChatPage() {
    if (chatFrame) post(chatFrame, "chat.page", { url: location.origin + location.pathname, title: document.title });
}

/** `sdchat` off this page's address (single-use), or null. */
function takeChatResumeParam(): string | null {
    try {
        const url = new URL(location.href);
        const link = url.searchParams.get("sdchat");
        if (!link) return null;
        url.searchParams.delete("sdchat");
        history.replaceState(history.state, "", url.toString());
        return link;
    } catch {
        return null;
    }
}

/** A resize can flip popup <-> sheet, which only the host can see. */
function onChatResize() {
    if (!chatFrame) return;
    const popup = chatPopupSize();
    post(chatFrame, "chat.view", { view: chatIsSheet() ? "sheet" : "popup", ...popup });
    if (chatOpen && chatIsSheet()) sizeChatFrame(0, 0);
}

function post(frame: HTMLIFrameElement, type: string, payload?: unknown) {
    frame.contentWindow?.postMessage({ ns: PROTOCOL_NAMESPACE, v: PROTOCOL_VERSION, type, payload }, embedOrigin);
}

// ---------------------------------------------------------------------------
// Buy dock
//
// The store's published buy dock on a product page: inline where `el` is, plus
// its sticky bar. On a Shopify theme the Storedum Buy Dock app block mounts it.
// The page's side of the frames (buydock.ts) ships as sdk/buydock.js, loaded on
// the first mount, so pages without a dock never download it.
// ---------------------------------------------------------------------------

let buyDockModule: BuyDockModule | null = null;
let buyDockWaiting: Array<(module: BuyDockModule) => void> = [];

function withBuyDock(fn: (module: BuyDockModule) => void) {
    if (buyDockModule) return fn(buyDockModule);
    buyDockWaiting.push(fn);
    if (buyDockWaiting.length > 1) return;
    const script = document.createElement("script");
    script.src = `${embedOrigin}/sdk/buydock.js`;
    script.async = true;
    script.onload = () => {
        buyDockModule = (window as any).__storedumBuyDock || null;
        if (!buyDockModule) return log("buydock.js loaded without the buy dock");
        const waiting = buyDockWaiting;
        buyDockWaiting = [];
        waiting.forEach((next) => next(buyDockModule!));
    };
    script.onerror = () => {
        buyDockWaiting = [];
        log("buydock.js failed to load");
    };
    document.head.appendChild(script);
}

/** Buy Now on Shopify: the drawer's Checkout path — session on the cart, sign-in offered when the store asks. */
function buyDockCheckout(provider?: string) {
    const driver = shopify;
    if (!driver) return;
    reportCheckout();
    if (driver.options.signin?.beforeCheckout && !signedIn && !driver.options.customerId) {
        heldCheckout = { provider };
        account.login();
        return;
    }
    bounded(stampCart(driver.cart)).then(() => driver.checkout(undefined, provider));
}

/** The bars' cart button: our drawer, or the theme's cart page where the drawer is off. */
function openBuyDockCart() {
    if (shopify && shopify.options.openOnAdd === false && shopify.options.interceptCartLinks === false) {
        window.location.assign(shopify.options.routes?.cart || "/cart");
        return;
    }
    cart.open();
}

/** A mount made before init (a page's effect can run before the app's), done in init. */
let queuedDock: BuyDockMountOptions | null = null;
/** Bumped by unmount, so a mount still waiting for buydock.js is dropped once its page moved on. */
let dockGeneration = 0;

export const buydock = {
    mount(opts: BuyDockMountOptions) {
        const store = options?.store;
        if (!store) {
            queuedDock = opts;
            return;
        }
        const generation = dockGeneration;
        withBuyDock((module) =>
            generation === dockGeneration &&
            module.mount(
                {
                    embedOrigin,
                    store,
                    fresh: freshParam(),
                    shopify: () => shopify,
                    // Cart writes carry the page's attribution, as storedum.cart.add does, plus the dock's own
                    // (its note of what the shopper saw, which /Cart/Add keeps for the order).
                    send: (type, payload) =>
                        send(
                            type,
                            (type.startsWith("cart.")
                                ? { ...(payload as object), attribution: { ...attribution(), ...((payload as { attribution?: object }).attribution || {}) } }
                                : payload) as typeof payload,
                        ),
                    notify,
                    openCart: openBuyDockCart,
                    checkout: buyDockCheckout,
                    lift: (px) => {
                        if (chatFrame) chatFrame.style.bottom = `${Math.max(0, Math.round(px))}px`;
                    },
                    log,
                },
                opts,
            ),
        );
    },
    /** A storefront that knows its own cart and variant says so (Shopify is read by the SDK itself). */
    update(input: BuyDockPageInput) {
        withBuyDock((module) => module.update(input));
    },
    unmount() {
        queuedDock = null;
        dockGeneration += 1;
        buyDockModule?.unmount();
    },
};

// ---------------------------------------------------------------------------
// Account page
//
// The shopper's account — orders, wishlist, store credit, profile, security —
// as an inline frame of account.html on the host's own account page, between
// its header and footer. The frame is as tall as its content (it reports that),
// so the page scrolls as one. What only the host can do it asks for: sign-in,
// and opening an order on the hosted order page.
//
// The desktop side panel can't stick inside that frame (it never scrolls), so
// it is a second frame on THIS page, in a track laid over the column the
// account leaves for it, kept in view by CSS sticky below the store's header:
// the bundle box's rail, the same way.
// ---------------------------------------------------------------------------

export interface AccountMountOptions {
    /** Where the account goes; its contents are replaced. */
    el: HTMLElement;
    /** Section to open: "orders", "wishlist", "store-credit", "profile" or "security". The overview by default. */
    route?: string | null;
}

let accountRoot: HTMLDivElement | null = null;
let accountFrame: HTMLIFrameElement | null = null;
let accountTrack: HTMLDivElement | null = null;
let accountPanel: HTMLIFrameElement | null = null;
/** The panel's column in the account frame's pixels, as it last said (`account:slot`). */
let accountSlot: { top: number; left: number; width: number; height: number } | null = null;
/** What the panel shows, as the account frame last sent it: handed to a panel frame that loads after. */
let accountPanelState: unknown = null;
let queuedAccount: AccountMountOptions | null = null;
let accountViewportFrame = 0;
/** How much of the top of the screen the store's own pinned bars cover (its sticky header), as last measured. */
let accountChrome = 0;
let accountChromeTimer: ReturnType<typeof setTimeout> | null = null;
let accountSettleTimer: ReturnType<typeof setTimeout> | null = null;

const ACCOUNT_PANEL_GAP = 24;
/** The panel card's hairline and shadow need room in its frame, so the frame is this much bigger than the column all round. */
const ACCOUNT_PANEL_BLEED = 4;

function postAccount(frame: HTMLIFrameElement | null, event: EventName, payload: unknown) {
    const message: EventMessage = { ns: PROTOCOL_NAMESPACE, v: PROTOCOL_VERSION, event, payload };
    frame?.contentWindow?.postMessage(message, embedOrigin);
}

/**
 * How much of the top of the screen the store keeps pinned (a sticky header,
 * an announcement bar above it): the panel sticks below it, a new section
 * starts below it, dialogs open below it. Hit-tests the top of the screen and
 * walks down through stacked bars, ignoring our own frames (the box's way).
 */
function measureAccountChrome(): number {
    if (typeof document.elementsFromPoint !== "function") return 0;
    const x = Math.round(window.innerWidth / 2);
    let y = 1;
    for (let i = 0; i < 5; i += 1) {
        const hit = document.elementsFromPoint(x, y).find((el) => !accountRoot?.contains(el));
        if (!hit) break;
        let node: Element | null = hit;
        let pinned: Element | null = null;
        while (node && node !== document.body && node !== document.documentElement) {
            const position = getComputedStyle(node).position;
            if (position === "fixed" || position === "sticky") {
                pinned = node;
                break;
            }
            node = node.parentElement;
        }
        if (!pinned) break;
        const rect = pinned.getBoundingClientRect();
        if (rect.top > y + 1 || rect.width < window.innerWidth * 0.6 || rect.bottom > window.innerHeight * 0.4) break;
        y = Math.ceil(rect.bottom) + 1;
    }
    return Math.max(0, y - 1);
}

function applyAccountChrome() {
    accountChrome = measureAccountChrome();
    if (accountPanel) accountPanel.style.top = `${accountChrome + ACCOUNT_PANEL_GAP - ACCOUNT_PANEL_BLEED}px`;
    sendAccountViewport();
}

/** Measured again once a header that slides away on scroll (and back) has finished moving, not mid-way. */
function remeasureAccountChrome() {
    if (!accountChromeTimer) {
        accountChromeTimer = setTimeout(() => {
            accountChromeTimer = null;
            applyAccountChrome();
        }, 200);
    }
    if (accountSettleTimer) clearTimeout(accountSettleTimer);
    accountSettleTimer = setTimeout(() => {
        accountSettleTimer = null;
        applyAccountChrome();
    }, 600);
}

function onAccountScroll() {
    remeasureAccountChrome();
    sendAccountViewport();
}

/**
 * Which part of the frame is on screen, for its dialogs and sheets. Inside a
 * frame as tall as its content, `position: fixed` means the whole frame, so a
 * centred dialog would open in the middle of a page-long frame, off screen.
 */
function sendAccountViewport() {
    if (!accountFrame?.contentWindow || accountViewportFrame) return;
    accountViewportFrame = requestAnimationFrame(() => {
        accountViewportFrame = 0;
        if (!accountFrame?.contentWindow) return;
        const rect = accountFrame.getBoundingClientRect();
        const top = Math.max(0, accountChrome - rect.top);
        const height = Math.max(0, Math.min(window.innerHeight, rect.bottom) - Math.max(accountChrome, rect.top));
        postAccount(accountFrame, "account:frame", { viewport: { top, height } });
    });
}

function makeAccountPanel() {
    const store = options?.store;
    if (!store || !accountTrack || accountPanel) return;
    const frame = document.createElement("iframe");
    frame.src = `${embedOrigin}/account.html?store=${encodeURIComponent(store)}&surface=panel${freshParam()}`;
    frame.title = "Account sections";
    frame.setAttribute("scrolling", "no");
    // At least a pixel: a zero-area cross-origin frame is throttled and stops reporting.
    // Glides when the store's header slides in or out above it, instead of jumping.
    frame.style.cssText =
        "display:block;width:100%;height:1px;border:0;margin:0;padding:0;background:transparent;color-scheme:normal;pointer-events:auto;" +
        `position:-webkit-sticky;position:sticky;top:${accountChrome + ACCOUNT_PANEL_GAP - ACCOUNT_PANEL_BLEED}px;transition:top 200ms ease-out;`;
    accountTrack.appendChild(frame);
    accountPanel = frame;
}

/** The track covers the panel's column (as tall as the account beside it), so the panel sticks down it and stops at its end. */
function placeAccountPanel() {
    if (!accountTrack) return;
    const s = accountSlot;
    if (!s) {
        accountTrack.style.display = "none";
        return;
    }
    makeAccountPanel();
    const b = ACCOUNT_PANEL_BLEED;
    accountTrack.style.left = `${s.left - b}px`;
    accountTrack.style.top = `${s.top - b}px`;
    accountTrack.style.width = `${s.width + b * 2}px`;
    accountTrack.style.height = `${s.height + b * 2}px`;
    accountTrack.style.display = "block";
}

function onAccountResize() {
    applyAccountChrome();
}

export const accountPage = {
    mount(opts: AccountMountOptions) {
        const store = options?.store;
        if (!store) {
            queuedAccount = opts;
            return;
        }
        accountPage.unmount();
        const route = String(opts.route || "").replace(/^[#/]+/, "");
        const root = document.createElement("div");
        root.style.cssText = "position:relative;width:100%;";
        const frame = document.createElement("iframe");
        // `panel=frame`: this page holds the side panel. An account loaded by an older SDK draws its own.
        frame.src = `${embedOrigin}/account.html?store=${encodeURIComponent(store)}&panel=frame${freshParam()}#/${encodeURI(route)}`;
        frame.title = "Your account";
        frame.style.cssText = "display:block;width:100%;height:640px;border:0;background:transparent;";
        // The track lets clicks through to the account; only the panel frame takes them.
        const track = document.createElement("div");
        track.style.cssText = "position:absolute;display:none;pointer-events:none;";
        root.append(frame, track);
        opts.el.replaceChildren(root);
        accountRoot = root;
        accountFrame = frame;
        accountTrack = track;
        // Wide screens show the panel: load it alongside the account instead of after its first layout.
        if (window.innerWidth >= 1024) makeAccountPanel();
        applyAccountChrome();
        window.addEventListener("scroll", onAccountScroll, { passive: true });
        window.addEventListener("resize", onAccountResize);
        document.addEventListener("transitionend", remeasureAccountChrome, { capture: true, passive: true });
        document.addEventListener("animationend", remeasureAccountChrome, { capture: true, passive: true });
    },
    unmount() {
        queuedAccount = null;
        window.removeEventListener("scroll", onAccountScroll);
        window.removeEventListener("resize", onAccountResize);
        document.removeEventListener("transitionend", remeasureAccountChrome, { capture: true });
        document.removeEventListener("animationend", remeasureAccountChrome, { capture: true });
        if (accountChromeTimer) clearTimeout(accountChromeTimer);
        if (accountSettleTimer) clearTimeout(accountSettleTimer);
        accountChromeTimer = accountSettleTimer = null;
        accountRoot?.remove();
        accountRoot = accountFrame = accountTrack = accountPanel = null;
        accountSlot = null;
        accountPanelState = null;
    },
};

// ---------------------------------------------------------------------------
// Bundle box
//
// The store's bundle box builder on its product page. On a Shopify theme the
// Storedum Bundle Box app block mounts it. The page's side of its frames
// (box.ts) ships as sdk/box.js, loaded on the first mount.
// ---------------------------------------------------------------------------

let boxModule: BoxModule | null = null;
let boxWaiting: Array<(module: BoxModule) => void> = [];

function withBox(fn: (module: BoxModule) => void) {
    if (boxModule) return fn(boxModule);
    boxWaiting.push(fn);
    if (boxWaiting.length > 1) return;
    const script = document.createElement("script");
    script.src = `${embedOrigin}/sdk/box.js`;
    script.async = true;
    script.onload = () => {
        boxModule = (window as any).__storedumBox || null;
        if (!boxModule) return log("box.js loaded without the box");
        const waiting = boxWaiting;
        boxWaiting = [];
        waiting.forEach((next) => next(boxModule!));
    };
    script.onerror = () => {
        boxWaiting = [];
        log("box.js failed to load");
    };
    document.head.appendChild(script);
}

let queuedBox: BoxMountOptions | null = null;
let boxGeneration = 0;

export const box = {
    mount(opts: BoxMountOptions) {
        const store = options?.store;
        if (!store) {
            queuedBox = opts;
            return;
        }
        const generation = boxGeneration;
        withBox((module) =>
            generation === boxGeneration &&
            module.mount(
                {
                    embedOrigin,
                    store,
                    fresh: freshParam(),
                    shopify: () => shopify,
                    notify,
                    openCart: openBuyDockCart,
                    log,
                },
                opts,
            ),
        );
    },
    unmount() {
        queuedBox = null;
        boxGeneration += 1;
        boxModule?.unmount();
    },
};

// ---------------------------------------------------------------------------
// Shoppable videos
//
// A video row, story circles, a product's videos or the floating video, each a
// frame of videos.html. On a Shopify theme the Storedum video blocks mount them;
// on our storefront the page builder does. The page side (videos.ts) ships as
// sdk/videos.js, loaded on the first mount.
// ---------------------------------------------------------------------------

let videosModule: VideosModule | null = null;
let videosWaiting: Array<(module: VideosModule) => void> = [];

function withVideos(fn: (module: VideosModule) => void) {
    if (videosModule) return fn(videosModule);
    videosWaiting.push(fn);
    if (videosWaiting.length > 1) return;
    const script = document.createElement("script");
    script.src = `${embedOrigin}/sdk/videos.js`;
    script.async = true;
    script.onload = () => {
        videosModule = (window as any).__storedumVideos || null;
        if (!videosModule) return log("videos.js loaded without the videos");
        const waiting = videosWaiting;
        videosWaiting = [];
        waiting.forEach((next) => next(videosModule!));
    };
    script.onerror = () => {
        videosWaiting = [];
        log("videos.js failed to load");
    };
    document.head.appendChild(script);
}

/** Mounts made before init, done in init. */
let queuedVideos: VideosMountOptions[] = [];

export const videos = {
    mount(opts: VideosMountOptions) {
        const store = options?.store;
        if (!store) {
            queuedVideos.push(opts);
            return;
        }
        withVideos((module) =>
            module.mount(
                {
                    embedOrigin,
                    store,
                    fresh: freshParam(),
                    shopify: () => shopify,
                    // Cart writes carry the page's attribution, plus the video's note (which /Cart/Add keeps for the order).
                    send: (type, payload) =>
                        send(
                            type,
                            (type.startsWith("cart.")
                                ? { ...(payload as object), attribution: { ...attribution(), ...((payload as { attribution?: object }).attribution || {}) } }
                                : payload) as typeof payload,
                        ),
                    openCart: openBuyDockCart,
                    checkout: buyDockCheckout,
                    log,
                },
                opts,
            ),
        );
    },
    unmount(el?: HTMLElement | null) {
        if (!el) queuedVideos = [];
        else queuedVideos = queuedVideos.filter((o) => o.el !== el);
        videosModule?.unmount(el);
    },
};

function shopifySummary(cart: { item_count?: number; total_price?: number }): CartSummary {
    return { total_items: cart.item_count ?? 0, total_price: (cart.total_price ?? 0) / 100 };
}

export function on(event: EventName, handler: (payload: any) => void): () => void {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event)!.add(handler);
    return () => listeners.get(event)?.delete(handler);
}

// No default export on purpose: the IIFE build assigns these named exports to
// `window.storedum` itself, so a Shopify theme gets `storedum.init(...)` from a
// plain <script> tag. Adding a default export would bolt a stray `.default`
// onto that global and overwrite any manual assignment we made here anyway.
